import { Suspense, useRef, useState, useEffect } from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   Box,
   Button,
   Flex,
   FormControl,
   FormHelperText,
   FormLabel,
   Heading,
   Input,
   InputGroup,
   InputProps,
   InputRightElement,
   Link,
   Skeleton,
   Switch,
   SwitchProps,
   Text,
   VStack,
} from '@chakra-ui/react';
import { _js } from '@ifixit/localize';
import { _jsr } from 'Shared/translations_react';
import { FaIcon } from '@ifixit/icons';
import {
   faScrewdriverWrench as faScrewdriverWrenchDuotone,
   faPenToSquare as faPenToSquareDuotone,
   faCertificate as faCertificateDuotone,
} from '@fortawesome/pro-duotone-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { Recaptcha } from 'Shared/recaptcha';
import { Auth } from 'Shared/auth';
import { fireGlobal, getLastPage, Geo, goToLastPage } from 'Shared/utils';
import { FormError } from '../FormError';
import { getErrorMessage } from '../LogIn/LogIn';
import { SSOLoginButtons } from '../SSOLoginButtons';

type JoinFormValues = {
   email: string;
   name: string;
   password: string;
   wantsDigestEmails: boolean;
   wantsNewsEmails: boolean;
};

type RegisterValues = {
   login: string;
   username: string;
   password1: string;
   inviteCode: string | null;
   wantsDigestEmails: boolean;
   wantsNewsEmails: boolean;
   captchaResult?: string;
};

function SignUpPasswordInput({ id, ...props }: InputProps) {
   const [show, setShow] = useState(false);
   const handleClick = () => setShow(!show);

   return (
      <Suspense fallback={<Skeleton minHeight="876px" />}>
         <FormLabel htmlFor={id}>{_js('Password')}</FormLabel>
         <InputGroup>
            <Input id={id} pr={5} type={show ? 'text' : 'password'} {...props} />
            <InputRightElement boxSize="36px">
               <Button
                  variant="unstyled"
                  onClick={handleClick}
                  display="inline-flex"
                  alignItems="center"
                  aria-label="Password visibility"
               >
                  <FaIcon icon={show ? faEyeSlash : faEye} boxSize="16px" color="gray.400" />
               </Button>
            </InputRightElement>
         </InputGroup>
         <FormHelperText color="gray.500" fontSize="md">
            {_js('Your password must be at least 10 characters long.')}
         </FormHelperText>
      </Suspense>
   );
}

function SignUpInput({
   field,
   label,
   helperText,
   isInvalid,
   id,
   isDisabled = false,
   ...inputProps
}: {
   field: InputProps;
   label: string;
   helperText: string;
   isInvalid?: boolean;
   id?: string;
   isDisabled?: boolean;
   inputProps?: InputProps;
}) {
   return (
      <>
         <FormLabel htmlFor={id}>{label}</FormLabel>
         <Input isInvalid={isInvalid} id={id} disabled={isDisabled} {...field} {...inputProps} />
         <FormHelperText color="gray.500" fontSize="md">
            {helperText}
         </FormHelperText>
      </>
   );
}

function SignUpSwitch({
   field,
   label,
   id,
   ...switchProps
}: {
   field: SwitchProps;
   label: string;
   id: string;
} & SwitchProps) {
   return (
      <>
         <Flex id={id} align="center" width="100%">
            <Box flex="1">
               <FormLabel margin={0} fontWeight="normal">
                  {label}
               </FormLabel>
            </Box>
            <Switch {...field} {...switchProps} aria-labelledby={id} marginLeft={2} />
         </Flex>
      </>
   );
}

export const Join = ({
   privacyPolicyUrl,
}: {
   privacyPolicyUrl: string;
}) => {
   const duotoneIconStyles = {
      boxSize: { base: '20px', sm: '24px' },
      color: 'brand.500',
      marginBlock: 'auto',
      marginRight: 2,
   };

   const privacyPolicyLink = (
      <Link href={privacyPolicyUrl} color="gray.900">
         {_js('Privacy Policy')}
      </Link>
   );

   const termsLink = (
      <Link href="/Info/Terms_of_Use" color="gray.900">
         {/* Translators: "Terms" as in Terms of Use */}
         {_js('Terms')}
      </Link>
   );

   const initialValues: JoinFormValues = {
      email: App.inviteEmail ?? '',
      name: '',
      password: '',
      wantsDigestEmails: false,
      wantsNewsEmails: false,
   };

   const emailFieldProvided = !!App.inviteEmail;

   const validate = (values: JoinFormValues) => {
      const errors: any = {};
      if (!Validation.username(values.name)) {
         errors.name = App.registerMessages.username_invalid;
      }
      if (values.password.length > 72) {
         errors.password = 'Passwords cannot exceed 72 characters';
      }
      return errors;
   };

   const handleSubmit = (
      values: JoinFormValues,
      { setSubmitting, setFieldError }: FormikHelpers<JoinFormValues>
   ) => {
      const registerValues: RegisterValues = {
         login: values.email,
         username: values.name,
         password1: values.password,
         inviteCode: App.inviteCode ?? null,
         wantsDigestEmails: values.wantsDigestEmails,
         wantsNewsEmails: values.wantsNewsEmails,
      };

      const registerPromise = Recaptcha.executeInvisible(
         recaptchaPlaceholderRef.current,
         App.recaptchaSiteKey,
         'register'
      ).then(
         (recaptchaResponse: any) =>
            new Promise<void>((resolve, reject) => {
               registerValues.captchaResult = recaptchaResponse;
               (Auth as any).sendRegister(registerValues, (response: any) => {
                  if (response.reason === 'email_verification_required') {
                     fireGlobal('login.success', response);
                     setSubmitting(false);
                     resolve();
                     window.location.href = '/Login/email_verification_sent';
                  } else if (response.loggedIn) {
                     if (App.which == 'ifixit') {
                        // From PixelComponent.php
                        (window as any).fbq('track', 'CompleteRegistration', {
                           content_name: 'Account',
                        });
                     }
                     fireGlobal('login.success', response);
                     setSubmitting(false);
                     shopifySsoAndGoToLastPage();
                     resolve();
                  } else {
                     fireGlobal('login.failure', response);
                     showError(response);
                     setSubmitting(false);
                     reject();
                  }
               });
            })
      );

      const UserLib = window.guide_constants.UserLib;
      const errorFields: any = {};
      errorFields[UserLib('ERR_PASSWORD_MISMATCH')] = 'password';
      errorFields[UserLib('ERR_PASSWORD_INVALID')] = 'password';
      errorFields[UserLib('ERR_USERNAME_INVALID')] = 'name';
      errorFields[UserLib('ERR_EMAIL_INVALID')] = 'email';
      errorFields[UserLib('ERR_EMAIL_EXISTS')] = 'email';
      errorFields[UserLib('ERR_EMAIL_VERIFICATION')] = 'email';
      errorFields[UserLib('ERR_DISALLOWED')] = 'email';
      errorFields[UserLib('ERR_USER_LIMIT')] = 'email';
      errorFields[UserLib('ERR_CAPTCHA_FAIL')] = 'email';

      const showError = (response: any) => {
         setFieldError(errorFields[response.reason], response.message);
      };

      LoadingIndicator.withPromise(registerPromise, {
         pendingMessage: _js('Registering your account...'),
         successMessage: _js('Registration successful'),
         failureMessage: _js('Registration unsuccessful'),
      });
   };

   const recaptchaPlaceholderRef = useRef<HTMLDivElement>(null);
   const [ssoError, setSsoError] = useState<string | null>(null);
   const ssoLoginHandler = (response: any) => {
      (Auth as any).clearCookies();

      if (response.loggedIn) {
         fireGlobal('login.success', response);
         shopifySsoAndGoToLastPage();
      } else {
         setSsoError(getErrorMessage(response));
         fireGlobal('login.failure', response);
      }
   };

   const [communityDigestChecked, setCommunityDigestChecked] = useState(false);
   const [newsletterChecked, setNewsletterChecked] = useState(false);

   return (
      <>
         <Box
            fontSize="16px"
            width={{ base: '100%', md: '416px' }}
            marginTop={{ base: 0, md: '30vh' }}
         >
            <Heading
               as="h2"
               fontSize={{ base: '24px', sm: '30px' }}
               fontWeight="medium"
               border="none"
               mt="0"
               lineHeight="normal"
            >
               {_js("Join the world's largest repair community")}
            </Heading>
            <VStack marginTop={6} spacing={4} align="flex-start">
               <Flex>
                  <FaIcon icon={faScrewdriverWrenchDuotone} {...duotoneIconStyles} />
                  {_js('Learn from 2.5 million like-minded fixers')}
               </Flex>
               <Flex>
                  <FaIcon icon={faPenToSquareDuotone} {...duotoneIconStyles} />
                  {_js('Share your repair expertise with step-by-step guides')}
               </Flex>
               <Flex>
                  <FaIcon icon={faCertificateDuotone} {...duotoneIconStyles} />
                  {_js('Participate to earn reputation and help fix the world')}
               </Flex>
            </VStack>
         </Box>
         <Box fontSize="16px" width={{ base: '100%', md: '384px' }}>
            <Flex justify="center" marginBottom={4}>
               <Box as="span">{_js('Been here before?')}</Box>
               &nbsp;
               <Link href="/Login" color="brand.500" fontWeight="medium">
                  {_js('Log In')}
               </Link>
               .
            </Flex>
            <VStack
               className="sign-up-wrapper"
               flexDirection="column"
               spacing={6}
               paddingY={8}
               paddingX={6}
               bgColor="white"
               borderWidth={1}
               borderColor="gray.300"
               borderRadius="base"
            >
               <SSOLoginButtons
                  loginHandler={ssoLoginHandler}
                  error={ssoError}
                  setError={setSsoError}
                  register={true}
               />
               <Box className="sign-up-form" width="100%">
                  <Formik
                     initialValues={initialValues}
                     onSubmit={handleSubmit}
                     validate={validate}
                     validateOnChange={true}
                     validateOnBlur={false}
                  >
                     <Form>
                        <VStack
                           spacing={6}
                           sx={{
                              '& > div': { width: '100%' },
                           }}
                        >
                           <Box>
                              <Field
                                 type="email"
                                 required
                                 autoComplete="section-register username"
                                 name="email"
                                 placeholder="albert@domain.com"
                              >
                                 {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.email)}>
                                       <SignUpInput
                                          field={field}
                                          label={_js('Email')}
                                          helperText={_js(
                                             "We'll use your email to send you updates on your community contributions."
                                          )}
                                          isInvalid={Boolean(form.errors.email)}
                                          id="reg-email"
                                          isDisabled={emailFieldProvided}
                                       />
                                       <FormError>{form.errors.email as any}</FormError>
                                    </FormControl>
                                 )}
                              </Field>
                           </Box>
                           <Box>
                              <Field
                                 type="text"
                                 required
                                 autoComplete="section-register name"
                                 name="name"
                                 placeholder="Albert Einstein"
                                 // Should match constants in UserLib.php
                                 minLength={3}
                                 maxLength={30}
                              >
                                 {({ field, form }: FieldProps) => (
                                    <FormControl
                                       isInvalid={form.touched.name && Boolean(form.errors.name)}
                                    >
                                       <SignUpInput
                                          field={field}
                                          label={_js('Display Name')}
                                          helperText={_js('This field will be visible to others.')}
                                          isInvalid={form.touched.name && Boolean(form.errors.name)}
                                          id="reg-username"
                                       />
                                       <FormError>{form.errors.name as any}</FormError>
                                    </FormControl>
                                 )}
                              </Field>
                           </Box>
                           <Box>
                              <Field
                                 required
                                 autoComplete="section-register new-password"
                                 name="password"
                                 minLength={10}
                              >
                                 {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.password)}>
                                       <SignUpPasswordInput
                                          isInvalid={Boolean(form.errors.password)}
                                          id="reg-password"
                                          {...field}
                                       />
                                       <FormError>{form.errors.password as any}</FormError>
                                    </FormControl>
                                 )}
                              </Field>
                           </Box>
                           <VStack spacing={4} align="flex-start" fontSize="md">
                              <Box fontWeight="medium">
                                 {_js('Level up your repair skills by subscribing to:')}
                              </Box>
                              <Field name="wantsDigestEmails" type="checkbox">
                                 {({ field, form }: any) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
                                    useEffect(() => {
                                       Geo.runEverywhereButEU(() => {
                                          form.setFieldValue('wantsDigestEmails', true);
                                          setCommunityDigestChecked(true);
                                       });
                                    }, []);

                                    return (
                                       <FormControl>
                                          <SignUpSwitch
                                             field={field}
                                             label={_js(
                                                "Monthly highlights from iFixit's online community"
                                             )}
                                             id="community-yes"
                                             isChecked={communityDigestChecked}
                                             onChange={e => {
                                                field.onChange(e);
                                                setCommunityDigestChecked(e.target.checked);
                                             }}
                                          />
                                       </FormControl>
                                    );
                                 }}
                              </Field>
                              <Field name="wantsNewsEmails" type="checkbox">
                                 {({ field, form }: any) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
                                    useEffect(() => {
                                       Geo.runEverywhereButEU(() => {
                                          form.setFieldValue('wantsNewsEmails', true);
                                          setNewsletterChecked(true);
                                       });
                                    }, []);

                                    return (
                                       <FormControl>
                                          <SignUpSwitch
                                             field={field}
                                             label={_js(
                                                'Weekly repair tips, Right to Repair news, and first look access to teardowns'
                                             )}
                                             id="newsletter-yes"
                                             isChecked={newsletterChecked}
                                             onChange={e => {
                                                field.onChange(e);
                                                setNewsletterChecked(e.target.checked);
                                             }}
                                          />
                                       </FormControl>
                                    );
                                 }}
                              </Field>
                           </VStack>
                           <Box>
                              {App.recaptchaSiteKey && (
                                 <Box
                                    ref={recaptchaPlaceholderRef}
                                    className="showRegister js-captcha-container captcha-container"
                                 ></Box>
                              )}
                              <Button variant="cta" width="full" type="submit" id="registerBtn">
                                 {_js('Create my account')}
                              </Button>
                           </Box>
                           <Text color="gray.500">
                              {_jsr(
                                 /* Translators: %1 and %2 are links to our Privacy Policy and Terms of Use */
                                 'By joining iFixit, you agree to our %1 and %2.',
                                 privacyPolicyLink,
                                 termsLink
                              )}
                           </Text>
                        </VStack>
                     </Form>
                  </Formik>
               </Box>
            </VStack>
         </Box>
      </>
   );
};

function shopifySsoAndGoToLastPage() {
   if (!App.shopifySsoRoute) {
      goToLastPage();
   }

   const ssoUrl = new URL(App.shopifySsoRoute);
   const lastPage = new URL(getLastPage(), window.location.origin);
   ssoUrl.searchParams.set('return_to', lastPage.toString());
   window.location.href = ssoUrl.href;
}

initializeChakraComponent('Join', Join);
